import { Alert, AlertType } from '@design/components/alert/Alert';
import { generatePath } from '@helpers/utils/paths/paths';
import { isPathMatches } from '@terminal/components/app-header/utils/navigation-utils';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import {
  List,
  WatchlistError,
} from '@terminal/stores/use-watchlist/watchlist-types';
import { DEFAULT_TAB } from '@terminal/views/watchlist-page/hooks/useWatchlistTable';
import { widgetPaths } from '@terminal/views/widgets/widget-utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

const TOAST_ID = 'create-watchlist';

const getWatchlistPath = (id: string) => {
  const path = `${appPaths.watchlist}/${id}`;
  const isWatchlistToWatchlist = location.pathname.startsWith(
    `${appPaths.watchlist}/`
  );

  if (!isWatchlistToWatchlist) {
    return path;
  }

  const activeTab = new URLSearchParams(location.search).get(
    queryKeys.activeTab
  );

  if (!activeTab || activeTab === DEFAULT_TAB) {
    return path;
  }

  return generatePath({
    basePath: path,
    params: { [queryKeys.activeTab]: activeTab },
  });
};

export const useWatchlistActions = () => {
  const { t } = useTranslation('widget');
  const [watchlistError, setWatchlistError] = useState<WatchlistError>();
  const [hasExceededUsageLimit, setHasExceededUsageLimit] = useState(false);

  const navigate = useNavigate();

  const {
    createList,
    updateListName,
    hasListWithName,
    activeModal,
    setActiveModal,
    deleteList,
    setActiveListId,
    error,
    createListLoading,
  } = useWatchlist(state => ({
    createList: state.createList,
    updateListName: state.updateListName,
    hasListWithName: state.hasListWithName,
    activeModal: state.activeModal,
    setActiveModal: state.setActiveModal,
    deleteList: state.deleteList,
    setActiveListId: state.setActiveListId,
    error: state.error,
    createListLoading: state.createListLoading,
  }));

  const { showToast, removeToast } = useToasts(state => ({
    showToast: state.showToast,
    removeToast: state.removeToast,
  }));

  const clearError = () => {
    setWatchlistError(undefined);
  };

  const handleError = (error: WatchlistError) => {
    setWatchlistError(error);
    return null;
  };

  const showWatchlistSavedToast = ({
    id,
    name,
    message,
    btnLabel,
  }: {
    id: string;
    name?: string;
    message?: string;
    btnLabel?: string;
  }) => {
    const onClose = () => removeToast(TOAST_ID);
    showToast({
      id: TOAST_ID,
      hideToast: true,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          withIcon
          onClose={onClose}
          type={AlertType.Success}
          message={
            message ||
            t('widget:watchlistTrigger.watchlist-created', {
              watchlistName: name,
            })
          }
          primaryBtn={{
            label: btnLabel || t('widget:watchlistTrigger.viewOrEditWatchlist'),
            onClick: () => {
              navigateToWatchlist({ id });
              onClose();
            },
          }}
        />
      ),
    });
  };

  const createNewList = async (
    name: string,
    tags: string[],
    unresolvedTags?: string[]
  ): Promise<string | null> => {
    clearError();

    if (hasListWithName(name)) {
      return handleError(WatchlistError.Duplicate);
    }

    const id = await createList(name, tags, unresolvedTags);
    const createListError = useWatchlist.getState().error;

    if (createListError?.status === 403) {
      setHasExceededUsageLimit(true);
      return null;
    }

    if (id === null) {
      return handleError(WatchlistError.ServerError);
    }

    const noToast = isPathMatches(['dashboard', 'watchlist', 'onboarding']);

    if (!noToast) {
      showWatchlistSavedToast({ id, name });
    }

    return id;
  };

  const renameList = async (id: string, name: string) => {
    if (hasListWithName(name)) {
      return handleError(WatchlistError.Duplicate);
    }

    const response = await updateListName(id, name);

    if (response.error) {
      return handleError(WatchlistError.ServerError);
    }

    return response.data;
  };

  const navigateToWatchlist = ({
    id,
    skipFallback,
    openInNewTab,
  }: {
    id?: string;
    skipFallback?: boolean;
    openInNewTab?: boolean;
  }) => {
    //grab the latest state of lists
    const newLists = useWatchlist.getState().lists as List[];
    const idToNavigateTo = id || (!skipFallback ? newLists[0]?.id : undefined);
    setActiveListId(idToNavigateTo);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('widget', widgetPaths.watchlistManagerModal);
    const to = idToNavigateTo
      ? getWatchlistPath(idToNavigateTo)
      : `${appPaths.base}?${searchParams.toString()}`;
    setActiveModal({ activeModal: undefined });

    if (openInNewTab) {
      window.open(to, '_blank', 'noopener,noreferrer');
    } else {
      navigate(to);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteList(id);

    if (!error) {
      return navigateToWatchlist({});
    } else {
      return handleError(WatchlistError.ServerError);
    }
  };

  const handleRename = async (id: string, name: string) => {
    const res = await renameList(id, name);

    if (res) {
      setActiveModal({ activeModal: undefined });
    }
  };

  const watchlistErrorText =
    watchlistError &&
    /* i18next-extract-mark-context-next-line ["duplicate", "serverError", "emptyName", "emptyList"] */
    t('widget:watchlist.watchlistError', {
      context: watchlistError,
    });

  return {
    hasExceededUsageLimit,
    createListLoading,
    watchlistError,
    watchlistErrorText,
    activeModal,
    createNewList,
    renameList,
    clearError,
    setActiveModal,
    handleDelete,
    handleRename,
    navigateToWatchlist,
    setWatchlistError,
    showWatchlistSavedToast,
  };
};
